import { motion } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import { MousePointerClick } from "lucide-react";
import Logo3D from "./Logo3D";
import { scrollToId } from "./SmoothScroll";
import { useLang } from "@/i18n";

export const Hero = () => {
  const { t } = useLang();
  const [revealed, setRevealed] = useState(false);
  const [hint, setHint] = useState(false);
  const morphedRef = useRef(false);

  // Reveal hero texts only once the 3D logo has finished assembling.
  useEffect(() => {
    const onAssembled = () => setRevealed(true);
    window.addEventListener("ag:logo-assembled", onAssembled);
    const fb = setTimeout(() => setRevealed(true), 7000);
    return () => {
      window.removeEventListener("ag:logo-assembled", onAssembled);
      clearTimeout(fb);
    };
  }, []);

  // Show the double-click hint after reveal; hide it once the user triggers a
  // morph — and never re-show it afterwards (guards the case where a morph
  // fires before the show-timeout has run).
  useEffect(() => {
    if (!revealed) return;
    const onMorph = () => {
      morphedRef.current = true;
      setHint(false);
    };
    window.addEventListener("ag:logo-morph", onMorph);
    const tt = setTimeout(() => {
      if (!morphedRef.current) setHint(true);
    }, 700);
    return () => {
      window.removeEventListener("ag:logo-morph", onMorph);
      clearTimeout(tt);
    };
  }, [revealed]);

  const EASE = [0.16, 1, 0.3, 1];

  return (
    <section
      id="hero"
      className="relative min-h-screen w-full flex flex-col items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* SEO: primary H1 (visually hidden, read by search engines) */}
      <h1 className="sr-only">Achim Gasteiger Architekt Sterzing Südtirol</h1>
      <div
        className="absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 45%, rgba(217,207,184,0.06), transparent 70%)",
        }}
      />
      <div className="absolute inset-0 z-0 opacity-[0.35] pointer-events-none">
        <div className="absolute top-0 bottom-0 left-1/4 w-px bg-white/[0.04]" />
        <div className="absolute top-0 bottom-0 left-2/4 w-px bg-white/[0.04]" />
        <div className="absolute top-0 bottom-0 left-3/4 w-px bg-white/[0.04]" />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: revealed ? 1 : 0 }}
        transition={{ duration: 1.5 }}
        className="relative z-10 mb-4 md:mb-2 w-full px-6 flex justify-center"
        data-testid="hero-kicker"
      >
        <span className="font-mono2 text-[9px] md:text-[11px] tracking-[0.25em] md:tracking-[0.5em] uppercase text-[var(--txt-dim)] text-center leading-relaxed max-w-full">
          {t.hero.kicker}
        </span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.3, ease: EASE }}
        className="relative z-10 w-full h-[42vh] md:h-[46vh] max-w-[1100px]"
        data-testid="hero-logo-3d"
      >
        <Logo3D />
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: hint ? 1 : 0, y: hint ? 0 : 4 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="absolute inset-x-0 top-[62%] flex items-center justify-center gap-1.5 pointer-events-none"
          data-testid="logo-hint"
        >
          <MousePointerClick className="h-2.5 w-2.5 text-[var(--txt-mut)]" strokeWidth={1.5} />
          <span className="font-mono2 text-[8px] tracking-[0.22em] uppercase text-[var(--txt-mut)]">
            {t.hero.logoHint}
          </span>
        </motion.div>
      </motion.div>

      <div className="relative z-10 mt-3 md:mt-4 px-6 text-center" data-testid="hero-headline">
        <span className="block overflow-hidden">
          <motion.span
            className="block font-body font-light text-base md:text-xl text-[var(--txt)]"
            initial={{ y: "110%" }}
            animate={{ y: revealed ? 0 : "110%" }}
            transition={{ duration: 1.5, delay: revealed ? 0.2 : 0, ease: EASE }}
          >
            {t.hero.tagline}
          </motion.span>
        </span>
        <span className="block overflow-hidden mt-2">
          <motion.span
            className="block font-mono2 text-[11px] md:text-xs tracking-[0.25em] uppercase text-[var(--txt-mut)]"
            initial={{ y: "110%" }}
            animate={{ y: revealed ? 0 : "110%" }}
            transition={{ duration: 1.5, delay: revealed ? 0.45 : 0, ease: EASE }}
          >
            {t.hero.credential}
          </motion.span>
        </span>
      </div>

      <motion.button
        onClick={() => scrollToId("ueber")}
        initial={{ opacity: 0 }}
        animate={{ opacity: revealed ? 1 : 0 }}
        transition={{ duration: 1.4, delay: revealed ? 0.85 : 0 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 group"
        data-testid="hero-scroll-cue"
        aria-label="scroll"
      >
        <span className="font-mono2 text-[9px] tracking-[0.4em] uppercase text-[var(--txt-mut)] group-hover:text-[var(--txt-dim)] transition-colors">
          {t.hero.scroll}
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="block h-8 w-px bg-gradient-to-b from-[var(--txt-dim)] to-transparent"
        />
      </motion.button>
    </section>
  );
};
